'use client'

import { usePageContext } from '@context/PageContext'
import { useThirdPartyContext } from '@context/ThirdPartyContext'
import { usePageSectionContext } from '@context/PageSectionContext'
import { useAppContext } from '@context/AppContext'
import { appEnv } from '@env/appEnv'
import { getTrackObject } from '@utils/trackUtils'
import { BotoLogger } from '@log/botoLogger'

interface Properties {
  [key: string]: unknown
}

const DEFAULT_ANALYTICS_PROPERTIES = {
  asaasSource: 'asaas',
  environment: appEnv.NEXT_PUBLIC_ENVIRONMENT
}

export function useAnalytics() {
  const { abTests, customerOriginChannel } = usePageContext()
  const { segment } = useThirdPartyContext()
  const { distinctId } = useAppContext()
  const sectionName = usePageSectionContext()

  const defaultAnalyticsOptions = { anonymousId: distinctId }

  const track = (
    action: string,
    target: string,
    goal: string,
    content: string,
    customSection?: string,
    destination?: string,
    extraTracks?: Properties,
    customerSignUpOriginChannel?: string
  ) => {
    if (!distinctId) {
      BotoLogger.warn('asaasDistinctId não foi definido. Tracking de ação não será enviado.')
      return
    }

    const nameSection = customSection ?? sectionName
    if (!nameSection) {
      BotoLogger.warn(`Action ${action} target ${target} goal ${goal} content ${content} sem seção`)
    }

    let trackObject = getTrackObject(nameSection ?? '', action, target, goal, content, destination, abTests)

    if (extraTracks) trackObject = { ...trackObject, ...extraTracks }

    trackObject = { ...trackObject, ...DEFAULT_ANALYTICS_PROPERTIES }

    segment?.track(customerSignUpOriginChannel ?? customerOriginChannel, trackObject, defaultAnalyticsOptions)
  }

  const page = (customPageName?: string, properties?: Properties) => {
    const category = 'Boto'
    const pageName = customPageName ?? customerOriginChannel
    const propertiesObject = { ...properties, ...DEFAULT_ANALYTICS_PROPERTIES }

    segment?.page(category, pageName, propertiesObject, defaultAnalyticsOptions)
  }

  const identify = (id: string) => {
    segment?.identify(id, DEFAULT_ANALYTICS_PROPERTIES, defaultAnalyticsOptions)
  }

  const alias = (to: string, from: string) => {
    const options = { ...defaultAnalyticsOptions, ...DEFAULT_ANALYTICS_PROPERTIES }

    segment?.alias(to, from, options)
  }

  return { track, page, identify, alias }
}
